import type { LevelData } from "../types/level";
import {
  SYMBOL_SPRITE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_SIZE,
  NOTE_OPTIONS_FONT_KEY,
  NOTE_OPTIONS_FONT_SIZE,
  ACTIVE_SIMSUN_ASCII_FONT,
  type BitmapFontLayoutProfile,
} from "./SymbolSequenceFont";
import { LayoutBitmapTextRenderer } from "./LayoutBitmapTextRenderer";

const FONT_SCALE = 1;
const TOP_OFFSET = 12;
const ROW_GAP = 4;
const PADDING_HORIZONTAL = 8;
const PADDING_VERTICAL = 8;
const BG_COLOR = 0x009688;
const BG_ALPHA = 0.3;
const GUESS_TEXT_COLOR = 0xffd54f;
const GUESS_LETTER_SPACING = 4;
const DEPTH_BG = 10;
const DEPTH_TEXT = 11;
const DEPTH_GUESS = 12;
const DEPTH_INSTRUCTION = 13;
const FLASH_INTERVAL_MS = 250;
const DEFAULT_SCRAMBLE_INTERVAL_MS = 50;
const DEFAULT_SCRAMBLE_TRACK_LENGTH = 32;
function toDisplaySequence(symbolSequence: readonly string[]): string {
  return symbolSequence
    .map((token) => {
      if (token === "3") {
        return "③";
      }

      if (token === "5") {
        return "⑤";
      }

      return token;
    })
    .join("");
}

export interface SymbolSequenceRenderOptions {
  readonly subLabel?: string;
  readonly bgColor?: number;
  readonly usePlainText?: boolean;
  readonly instructionText?: string;
  readonly instructionFontProfile?: BitmapFontLayoutProfile;
  readonly textTopY?: number;
  readonly hideSequenceText?: boolean;
  readonly scramble?: SymbolSequenceScrambleState | undefined;
}

export interface SymbolSequenceScrambleState {
  readonly scrambledIndices: readonly number[];
  readonly glyphs: readonly string[];
  readonly intervalMs?: number;
  readonly trackLength?: number;
}

interface ScrambleSlotState {
  readonly track: readonly string[];
  readonly phaseOffset: number;
}

interface SequenceGlyphRenderItem {
  readonly text: Phaser.GameObjects.BitmapText;
  readonly width: number;
}

interface SequenceBounds {
  readonly global: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}

export class SymbolSequenceRenderer {
  private readonly scene: Phaser.Scene;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly text: Phaser.GameObjects.BitmapText;
  private readonly guessText: Phaser.GameObjects.BitmapText;
  private readonly viewportWidth: number;

  /**
   * Temporary local flash path for background color override.
   * Another branch already supports explicit background color overrides;
   * this should be reconciled with that API when the branches merge.
   */
  private bgOverrideColor: number | null = null;
  private bgOverrideAlpha: number | null = null;
  private plainTextActive: boolean = false;
  private readonly instructionText: LayoutBitmapTextRenderer;
  private instructionContent: string = "";
  private flashElapsed: number = 0;
  private readonly flashColors: readonly number[] = [0xff0000, 0x00ff00, 0x0000ff, 0xffffff];
  private scrambleElapsed: number = 0;
  private scrambleState: SymbolSequenceScrambleState | null = null;
  private currentSequence: readonly string[] = [];
  private currentPlainText: boolean = false;
  private currentHideSequenceText: boolean = false;
  private readonly scrambleSlots = new Map<number, ScrambleSlotState>();
  private readonly mixedGlyphTexts: Phaser.GameObjects.BitmapText[] = [];
  private mixedGlyphActive: boolean = false;

  public constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.viewportWidth = scene.cameras.main.width;

    this.bg = scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(DEPTH_BG);

    this.text = scene.add.bitmapText(
      0,
      TOP_OFFSET + PADDING_VERTICAL,
      SYMBOL_SPRITE_FONT_KEY,
      "",
      SYMBOL_SEQUENCE_FONT_SIZE,
    );
    this.text.setOrigin(0.5, 0);
    this.text.setPosition(this.viewportWidth / 2, TOP_OFFSET + PADDING_VERTICAL);
    this.text.setScrollFactor(0);
    this.text.setDepth(DEPTH_TEXT);

    this.guessText = scene.add.bitmapText(
      this.viewportWidth / 2,
      0,
      NOTE_OPTIONS_FONT_KEY,
      "",
      NOTE_OPTIONS_FONT_SIZE,
    );
    this.guessText.setOrigin(0.5, 0);
    this.guessText.setScrollFactor(0);
    this.guessText.setDepth(DEPTH_GUESS);
    this.guessText.setTint(GUESS_TEXT_COLOR);
    this.guessText.setLetterSpacing(GUESS_LETTER_SPACING);

    this.instructionText = new LayoutBitmapTextRenderer(scene, {
      depth: DEPTH_INSTRUCTION,
      tint: this.flashColors[0] ?? 0xffffff,
      fontProfile: ACTIVE_SIMSUN_ASCII_FONT,
    });
  }

  public render(levelData: LevelData, options?: SymbolSequenceRenderOptions): void {
    this.renderFromSequence(levelData.symbolSequence, options);
  }

  public renderFromSequence(
    sequence: readonly string[],
    options?: SymbolSequenceRenderOptions,
  ): void {
    const subLabel = options?.subLabel;
    const usePlainText = options?.usePlainText ?? false;
    const textTopY = options?.textTopY ?? (TOP_OFFSET + PADDING_VERTICAL);
    const hideSequenceText = options?.hideSequenceText ?? false;

    if (usePlainText !== this.plainTextActive) {
      this.text.setFont(usePlainText ? SYMBOL_SEQUENCE_FONT_KEY : SYMBOL_SPRITE_FONT_KEY);
      this.plainTextActive = usePlainText;
    }

    this.currentSequence = sequence;
    this.currentPlainText = usePlainText;
    this.currentHideSequenceText = hideSequenceText;
    this.setScrambleState(options?.scramble ?? null);
    this.text.setScale(FONT_SCALE);
    this.text.setPosition(this.viewportWidth / 2, textTopY);
    const mainBounds = this.renderSequence(sequence, usePlainText);
    this.text.setAlpha(hideSequenceText || this.mixedGlyphActive ? 0 : 1);
    if (hideSequenceText && this.mixedGlyphActive) {
      this.hideMixedGlyphTexts();
    }

    const bounds = mainBounds.global;
    let componentBottom: number;

    if (subLabel !== undefined && subLabel !== "") {
      const guessY = bounds.y + bounds.height + ROW_GAP;
      this.guessText.setText(subLabel);
      this.guessText.setPosition(this.viewportWidth / 2, guessY);

      const guessBounds = this.guessText.getTextBounds(true).global;
      const contentHeight = (guessBounds.y + guessBounds.height) - bounds.y;

      this.bg.clear();
      const bgColor = this.bgOverrideColor ?? options?.bgColor ?? BG_COLOR;
      const bgAlpha = this.bgOverrideAlpha ?? BG_ALPHA;
      this.bg.fillStyle(bgColor, bgAlpha);
      this.bg.fillRect(
        bounds.x - PADDING_HORIZONTAL,
        bounds.y - PADDING_VERTICAL,
        Math.max(bounds.width, guessBounds.width) + PADDING_HORIZONTAL * 2,
        contentHeight + PADDING_VERTICAL * 2,
      );

      this.guessText.setAlpha(1);
      componentBottom = guessBounds.y + guessBounds.height + PADDING_VERTICAL;
    } else {
      this.bg.clear();
      const bgColor = this.bgOverrideColor ?? options?.bgColor ?? BG_COLOR;
      const bgAlpha = this.bgOverrideAlpha ?? BG_ALPHA;
      this.bg.fillStyle(bgColor, bgAlpha);
      this.bg.fillRect(
        bounds.x - PADDING_HORIZONTAL,
        bounds.y - PADDING_VERTICAL,
        bounds.width + PADDING_HORIZONTAL * 2,
        bounds.height + PADDING_VERTICAL * 2,
      );

      this.guessText.setText("");
      this.guessText.setAlpha(0);
      componentBottom = bounds.y + bounds.height + PADDING_VERTICAL;
    }

    const instruction = options?.instructionText ?? "";
    if (instruction !== this.instructionContent) {
      this.instructionContent = instruction;
    }

    if (instruction.length > 0) {
      this.instructionText.setFontProfile(
        options?.instructionFontProfile ?? ACTIVE_SIMSUN_ASCII_FONT,
      );
      this.instructionText.render(instruction, {
        centerX: this.viewportWidth / 2,
        y: componentBottom + ROW_GAP,
      });
      this.instructionText.setAlpha(1);
    } else {
      this.instructionText.clear();
    }
  }

  public renderSequence(
    sequence: readonly string[],
    plainText: boolean = false,
  ): SequenceBounds {
    const displaySequence = this.applyScramble(sequence);
    const shouldUseMixedGlyphs = plainText && this.scrambleState !== null;
    this.mixedGlyphActive = shouldUseMixedGlyphs;

    if (shouldUseMixedGlyphs) {
      this.text.setText("");
      this.text.setAlpha(0);
      const bounds = this.renderMixedGlyphSequence(sequence, displaySequence);
      return bounds;
    }

    this.hideMixedGlyphTexts();
    const display = plainText ? displaySequence.join("") : toDisplaySequence(displaySequence);
    this.text.setText(display);
    this.text.setAlpha(1);
    return this.toSequenceBounds(this.text.getTextBounds(true).global);
  }

  public update(delta: number): void {
    if (this.scrambleState !== null) {
      this.scrambleElapsed += delta;
      this.renderSequence(this.currentSequence, this.currentPlainText);
      if (this.currentHideSequenceText && this.mixedGlyphActive) {
        this.hideMixedGlyphTexts();
      }
    }

    if (this.instructionContent.length === 0) {
      return;
    }
    this.flashElapsed += delta;
    const colorIndex = Math.floor(this.flashElapsed / FLASH_INTERVAL_MS) % this.flashColors.length;
    this.instructionText.setTint(this.flashColors[colorIndex] ?? 0xffffff);
  }

  /**
   * Temporarily override the background color for the next render.
   * Call clearBackgroundOverride() to reset.
   *
   * NOTE: Another branch has a more formal background-color override API.
   * This local implementation should be reconciled with that branch.
   */
  public setBackgroundOverride(color: number, alpha: number = BG_ALPHA): void {
    this.bgOverrideColor = color;
    this.bgOverrideAlpha = alpha;
  }

  public clearBackgroundOverride(): void {
    this.bgOverrideColor = null;
    this.bgOverrideAlpha = null;
  }

  public setVisible(visible: boolean): void {
    this.bg.setVisible(visible);
    this.text.setVisible(visible);
    this.guessText.setVisible(visible);
    this.instructionText.setVisible(visible);
  }

  public clearTransientText(): void {
    this.guessText.setText("");
    this.guessText.setAlpha(0);
    this.instructionContent = "";
    this.instructionText.clear();
    this.setScrambleState(null);
  }

  private setScrambleState(scramble: SymbolSequenceScrambleState | null): void {
    if (scramble === null || scramble.scrambledIndices.length === 0 || scramble.glyphs.length === 0) {
      this.scrambleState = null;
      this.scrambleSlots.clear();
      this.scrambleElapsed = 0;
      this.hideMixedGlyphTexts();
      return;
    }

    this.scrambleState = scramble;

    const activeIndices = new Set(scramble.scrambledIndices);
    for (const index of this.scrambleSlots.keys()) {
      if (!activeIndices.has(index)) {
        this.scrambleSlots.delete(index);
      }
    }

    for (const index of activeIndices) {
      if (!this.scrambleSlots.has(index)) {
        this.scrambleSlots.set(index, this.createScrambleSlot(scramble));
      }
    }
  }

  private createScrambleSlot(scramble: SymbolSequenceScrambleState): ScrambleSlotState {
    const trackLength = Math.max(1, scramble.trackLength ?? DEFAULT_SCRAMBLE_TRACK_LENGTH);
    const glyphs = scramble.glyphs;
    const track: string[] = [];

    for (let i = 0; i < trackLength; i += 1) {
      const previous = track[track.length - 1];
      let next = glyphs[Math.floor(Math.random() * glyphs.length)] ?? "";
      if (glyphs.length > 1 && next === previous) {
        const previousIndex = glyphs.indexOf(previous);
        const fallbackIndex = previousIndex >= 0 ? (previousIndex + 1) % glyphs.length : 0;
        next = glyphs[fallbackIndex] ?? next;
      }
      track.push(next);
    }

    return {
      track,
      phaseOffset: Math.floor(Math.random() * trackLength),
    };
  }

  private applyScramble(sequence: readonly string[]): readonly string[] {
    const scramble = this.scrambleState;
    if (scramble === null) {
      return sequence;
    }

    const intervalMs = Math.max(1, scramble.intervalMs ?? DEFAULT_SCRAMBLE_INTERVAL_MS);
    const output = [...sequence];

    for (const index of scramble.scrambledIndices) {
      if (index < 0 || index >= output.length) {
        continue;
      }

      const slot = this.scrambleSlots.get(index);
      if (slot === undefined || slot.track.length === 0) {
        continue;
      }

      const frame = Math.floor(this.scrambleElapsed / intervalMs);
      output[index] = slot.track[(frame + slot.phaseOffset) % slot.track.length] ?? output[index] ?? "";
    }

    return output;
  }

  private renderMixedGlyphSequence(
    sourceSequence: readonly string[],
    displaySequence: readonly string[],
  ): SequenceBounds {
    const items: SequenceGlyphRenderItem[] = [];
    const scrambledIndices = new Set(this.scrambleState?.scrambledIndices ?? []);

    for (let index = 0; index < displaySequence.length; index += 1) {
      const glyphText = this.getMixedGlyphText(index);
      const isScrambled = scrambledIndices.has(index);
      glyphText.setFont(isScrambled ? SYMBOL_SPRITE_FONT_KEY : SYMBOL_SEQUENCE_FONT_KEY);
      glyphText.setText(displaySequence[index] ?? sourceSequence[index] ?? "");
      glyphText.setScale(FONT_SCALE);
      glyphText.setAlpha(1);
      glyphText.setVisible(true);

      const bounds = glyphText.getTextBounds(true).global;
      items.push({ text: glyphText, width: bounds.width });
    }

    for (let index = displaySequence.length; index < this.mixedGlyphTexts.length; index += 1) {
      this.mixedGlyphTexts[index]?.setVisible(false);
    }

    const totalWidth = items.reduce((sum, item) => sum + item.width, 0);
    const textY = this.text.y;
    let cursorX = (this.viewportWidth - totalWidth) / 2;
    let maxHeight = 0;

    for (const item of items) {
      item.text.setPosition(cursorX, textY);
      item.text.setOrigin(0, 0);
      const bounds = item.text.getTextBounds(true).global;
      maxHeight = Math.max(maxHeight, bounds.height);
      cursorX += item.width;
    }

    return {
      global: {
        x: (this.viewportWidth - totalWidth) / 2,
        y: textY,
        width: totalWidth,
        height: maxHeight,
      },
    };
  }

  private getMixedGlyphText(index: number): Phaser.GameObjects.BitmapText {
    const existing = this.mixedGlyphTexts[index];
    if (existing !== undefined) {
      return existing;
    }

    const glyphText = this.scene.add.bitmapText(
      0,
      0,
      SYMBOL_SEQUENCE_FONT_KEY,
      "",
      SYMBOL_SEQUENCE_FONT_SIZE,
    );
    glyphText.setScrollFactor(0);
    glyphText.setDepth(DEPTH_TEXT);
    this.mixedGlyphTexts[index] = glyphText;
    return glyphText;
  }

  private hideMixedGlyphTexts(): void {
    this.mixedGlyphActive = false;
    for (const glyphText of this.mixedGlyphTexts) {
      glyphText.setVisible(false);
      glyphText.setAlpha(0);
    }
  }

  private toSequenceBounds(
    global: Phaser.Types.GameObjects.BitmapText.GlobalBitmapTextSize,
  ): SequenceBounds {
    return {
      global: {
        x: global.x,
        y: global.y,
        width: global.width,
        height: global.height,
      },
    };
  }

  public destroy(): void {
    this.bg.destroy();
    this.text.destroy();
    this.guessText.destroy();
    this.instructionText.destroy();
    for (const glyphText of this.mixedGlyphTexts) {
      glyphText.destroy();
    }
  }
}
